import {
  AxisChartByNumberPrediction,
  ChartDataType,
  ChartDisplayType
} from 'features/charts/chart.models';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import ChartBridge from 'features/charts/strategies/chart-bridge';
import {
  createDataPointSetByNumberPredictions,
  createDataPointSetsByNumberOptimisation
} from './charts/diminishing-returns-chart.helpers';
import Slider from 'common/components/inputs/slider';
import Button from 'common/components/buttons/button';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import {
  useGetDataScienceMmmChartResultsQuery,
  useGetDataScienceMmmConfigQuery
} from 'app/apis/queryApi';
import { MmmModelChartPlotOutputDto } from './models/mmm-model-output';
import Loading from 'common/components/loading/loading';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectIsEditingCurrentSelectedBudgetScenario,
  selectUpdatedBudgetScenarios,
  upsertBudgetScenarioValues
} from './edit-budget-scenario.slice';
import { selectDefaultAvailableModelTypes } from './mmm-controls/mmm-config.selectors';
import { RootState } from 'app/store';

export interface MmmDiminishingReturnsValueEditorProps {
  siteId: string;
  modelRunId: string;
  modelRunDate: string;
  segmentKey: string | undefined;
  segmentValue: string | undefined;
  budgetScenarioId: string;
  scenarioName: string;
  timeRangeType: string | undefined;
  currentSpend: number;
  onEditConfirmed: () => void;
}

const getClosestPlotIndex = (
  plots: MmmModelChartPlotOutputDto[] | undefined,
  targetSpend: number
): number => {
  if (!plots || plots.length === 0) return 0;

  return plots.reduce((closestIdx, plot, idx, arr) => {
    const closestDiff = Math.abs(arr[closestIdx].spend - targetSpend);
    const currentDiff = Math.abs(plot.spend - targetSpend);
    return currentDiff < closestDiff ? idx : closestIdx;
  }, 0);
};

const MmmDiminishingReturnsValueEditor: FC<
  MmmDiminishingReturnsValueEditorProps
> = ({
  siteId,
  modelRunId,
  modelRunDate,
  scenarioName,
  budgetScenarioId,
  segmentKey,
  segmentValue,
  timeRangeType,
  currentSpend,
  onEditConfirmed
}) => {
  const hasRunRef = useRef(false);
  const dispatch = useDispatch();

  const isCurrentlyEditingChosenScenario = useSelector((state: RootState) =>
    selectIsEditingCurrentSelectedBudgetScenario(state, {
      budgetId: budgetScenarioId,
      scenarioName
    })
  );

  const updatedBudgetScenario = useSelector((state: RootState) =>
    selectUpdatedBudgetScenarios(state, {
      budgetId: budgetScenarioId,
      scenarioName
    })
  );

  const { availableDefaultModelTypes } = useGetDataScienceMmmConfigQuery(
    {
      siteId
    },
    {
      selectFromResult: (result) => {
        const { isLoading, isFetching } = result;
        return {
          ...result,
          isLoading,
          isFetching,
          availableDefaultModelTypes: selectDefaultAvailableModelTypes(
            result,
            isFetching,
            segmentKey
          )
        };
      }
    }
  );

  const { data: mmmChartData, isFetching: isFetchingChartData } =
    useGetDataScienceMmmChartResultsQuery(
      {
        siteId,
        modelRunId,
        modelRunDate,
        segment: segmentKey ?? '',
        segmentValue: segmentValue ?? '',
        modelType: isCurrentlyEditingChosenScenario
          ? updatedBudgetScenario?.originalScenarioName ?? scenarioName
          : scenarioName,
        timeRangeType: timeRangeType ?? ''
      },
      { skip: !segmentKey || !segmentValue || !modelRunId || !modelRunDate }
    );

  const [sliderIndex, setSliderIndex] = useState<number>(() => {
    if (!mmmChartData?.chartPlots?.length) return 0;
    return getClosestPlotIndex(mmmChartData.chartPlots, currentSpend);
  });

  // Only runs once the component has correctly mounted the currentSpend and got the chart data.
  useEffect(() => {
    if (mmmChartData?.chartPlots?.length && currentSpend) {
      // Prevents re-running after the first render
      if (hasRunRef.current) return;

      hasRunRef.current = true; // Set the flag to true after the first run
      const initial = getClosestPlotIndex(
        mmmChartData.chartPlots,
        currentSpend
      );
      setSliderIndex(initial);
    }
  }, [hasRunRef, currentSpend, mmmChartData]);

  const nearestSpendToReturnValues = useMemo(() => {
    if (
      !mmmChartData ||
      mmmChartData.chartPlots.length === 0 ||
      currentSpend === null ||
      currentSpend === undefined
    )
      return 0;

    return mmmChartData.chartPlots[sliderIndex].spend; // return actual spend value
  }, [mmmChartData, sliderIndex, currentSpend]);

  const handleChange = (newSpendIndex: number) => {
    setSliderIndex(newSpendIndex);
  };

  const chart: AxisChartByNumberPrediction = useMemo(
    () => ({
      id: 'spend-vs-revenue',
      title: 'Spend vs Revenue',
      type: ChartDataType.NUMBER_PREDICTION,
      tooltipFormat: new Intl.NumberFormat('en-GB', {
        style: 'decimal',
        maximumFractionDigits: 2
      }),
      shouldShowChartTypeDropdown: false,
      defaultChartDisplayType: ChartDisplayType.LINE,
      xAxisTitle: 'Spend',
      yAxisTitle: 'Revenue',
      yAxisFormat: new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
      }),
      dataPointSetWithPredictions:
        createDataPointSetByNumberPredictions(mmmChartData),
      dataPointSetWithOptimisations: []
    }),
    [mmmChartData]
  );

  const diminishingReturnsChart = useMemo(() => {
    const dataPointSet = createDataPointSetsByNumberOptimisation(
      mmmChartData
    ).filter(
      (dataPointSet) =>
        dataPointSet.dataPointSetName === mmmChartData?.segmentValue &&
        dataPointSet.xValue === nearestSpendToReturnValues
    );

    return {
      ...chart,
      dataPointSetWithOptimisations: dataPointSet
    };
  }, [mmmChartData, chart, nearestSpendToReturnValues]);

  const comparisonBetweenChange = useMemo(() => {
    if (!mmmChartData) return null;

    const plots = mmmChartData.chartPlots;

    // Find the closest spend index in the chartPlots
    const getSpend = (index: number) => plots[index]?.spend ?? 0;
    const getRevenue = (index: number) => plots[index]?.revenue ?? 0;

    const initialIndex = getClosestPlotIndex(plots, currentSpend);
    const adjustedIndex = sliderIndex;

    const initialSpend = getSpend(initialIndex);
    const adjustedSpend = getSpend(adjustedIndex);

    const initialRevenue = getRevenue(initialIndex);
    const adjustedRevenue = getRevenue(adjustedIndex);

    const initialROAS = initialSpend ? initialRevenue / initialSpend : 0;
    const adjustedROAS = adjustedSpend ? adjustedRevenue / adjustedSpend : 0;

    const nextInitialIndex = initialIndex + 1;
    const nextAdjustedIndex = adjustedIndex + 1;

    const nextInitialSpend = getSpend(nextInitialIndex);
    const nextInitialRevenue = getRevenue(nextInitialIndex);

    const nextAdjustedSpend = getSpend(nextAdjustedIndex);
    const nextAdjustedRevenue = getRevenue(nextAdjustedIndex);

    const initialmROAS =
      nextInitialSpend !== initialSpend
        ? (nextInitialRevenue - initialRevenue) /
          (nextInitialSpend - initialSpend)
        : initialROAS;

    const adjustedmROAS =
      nextAdjustedSpend !== adjustedSpend && nextAdjustedIndex < plots.length
        ? (nextAdjustedRevenue - adjustedRevenue) /
          (nextAdjustedSpend - adjustedSpend)
        : adjustedROAS;

    return {
      initialSpend,
      adjustedSpend,
      initialRevenue,
      adjustedRevenue,
      initialROAS,
      adjustedROAS,
      initialmROAS,
      adjustedmROAS
    };
  }, [mmmChartData, currentSpend, sliderIndex]);

  if (
    isFetchingChartData ||
    !mmmChartData ||
    mmmChartData.chartPlots.length === 0 ||
    currentSpend == null
  ) {
    return <Loading />;
  }

  return (
    <>
      <div className="flex gap-12 p-2 ml-6 space-y-4">
        <div style={{ height: 320, width: 640 }}>
          <ChartBridge chart={diminishingReturnsChart} />
        </div>
        <div>
          {comparisonBetweenChange && (
            <table className="min-w-full table-auto bg-white rounded-md shadow-sm overflow-hidden">
              <caption className="sr-only">
                Comparison of current and adjusted spend metrics
              </caption>
              <thead className="bg-neutral-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-sm font-semibold text-neutral-700 rounded-tl-md"
                  >
                    Metric
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-sm font-semibold text-neutral-700"
                  >
                    Current Spend
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-sm font-semibold text-neutral-700 rounded-tr-md"
                  >
                    Adjusted Spend
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                <tr className="hover:bg-neutral-50">
                  <td className="px-6 py-4 text-sm font-medium text-neutral-800">
                    Spend
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-600">
                    {comparisonBetweenChange.initialSpend.toLocaleString(
                      'en-GB',
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      }
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-600">
                    {comparisonBetweenChange.adjustedSpend.toLocaleString(
                      'en-GB',
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}
                  </td>
                </tr>
                <tr className="hover:bg-neutral-50">
                  <td className="px-6 py-4 text-sm font-medium text-neutral-800">
                    Revenue
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-600">
                    {comparisonBetweenChange.initialRevenue.toLocaleString(
                      'en-GB',
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-600">
                    {comparisonBetweenChange.adjustedRevenue.toLocaleString(
                      'en-GB',
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}
                  </td>
                </tr>
                <tr className="hover:bg-neutral-50">
                  <td className="px-6 py-4 text-sm font-medium text-neutral-800">
                    ROAS
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-600">
                    {comparisonBetweenChange.initialROAS.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-600">
                    {comparisonBetweenChange.adjustedROAS.toFixed(2)}
                  </td>
                </tr>
                <tr className="hover:bg-neutral-50">
                  <td className="px-6 py-4 text-sm font-medium text-neutral-800 rounded-bl-md">
                    mROAS
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-600">
                    {comparisonBetweenChange.initialmROAS.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-sm text-neutral-600 rounded-br-md">
                    {comparisonBetweenChange.adjustedmROAS.toFixed(2)}
                  </td>
                </tr>
              </tbody>
              <tfoot className="bg-neutral-50">
                <tr>
                  <td
                    colSpan={3}
                    className="px-6 py-3 text-xs text-neutral-500 italic"
                  >
                    Comparison between current and adjusted spend metrics
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
      <span className="flex items-center gap-4">
        <label htmlFor="spend-slider" className="ml-2 text-sm font-medium">
          Adjust Spend:
        </label>
        <Slider
          className="ml-2 w-1/3"
          step={1}
          min={0}
          max={mmmChartData.chartPlots.length - 1}
          value={sliderIndex}
          onChange={(newChartIndex) => handleChange(newChartIndex)}
        />
        <span className="text-sm w-10 mr-10">
          {comparisonBetweenChange?.adjustedSpend.toLocaleString()}
        </span>
        <Button
          classOverrides="ml-10 px-4 py-2.5"
          theme={ButtonTheme.PRIMARY}
          identifier={'apply-edit-changes-button'}
          onClick={() => {
            onEditConfirmed();
            dispatch(
              upsertBudgetScenarioValues({
                availableSystemModels: availableDefaultModelTypes,
                modelRunName: modelRunId,
                timeType: timeRangeType!,
                segmentKey: segmentKey!,
                segmentValue: segmentValue!,
                scenarioName,
                newSpendValue: comparisonBetweenChange?.adjustedSpend ?? 0,
                newRevenueValue: comparisonBetweenChange?.adjustedRevenue ?? 0,
                newRoasValue: comparisonBetweenChange?.adjustedROAS ?? 0,
                newMRoasValue: comparisonBetweenChange?.adjustedmROAS ?? 0
              })
            );
          }}
        >
          <p>Apply Changes</p>
        </Button>
      </span>
      <br />
    </>
  );
};

export default MmmDiminishingReturnsValueEditor;
